/**
 * ADD SEARCH AD GROUPS — /google-ads/<leads|traffic>/create/adgroup[?campaign_id=…]
 *
 * One existing Search campaign, many ad-group drafts — each with keywords,
 * negatives and its responsive search ads — created in order. Same cards,
 * AI actions and live preview as the campaign workspace. Resumable: created
 * groups and ads are remembered by draft id.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Check, Globe, Plus, Sparkles, Wand2 } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import { googleAdsService, GoogleAdsCampaignSummary, SearchObjectiveKey } from '@/services/google-ads.service';
import {
  BackTo, biddingLabel, ErrorBox, errMsg, formatMicros, Pill, Section, statusOf, Tag, useYoutubeAuth,
} from '@/components/google-ads/youtube/youtubeShared';
import { isHttpUrl, Pub, toMicros } from '@/components/google-ads/objectives/objectiveShared';
import {
  cloneSearchGroup, isManualCpc, isSearchCampaign, newSearchGroup, rsaIssues, SearchAdPreview, SearchGroupDraft, searchGroupIssues,
  searchObjectiveOf, SearchObjectiveConfig,
} from './searchShared';
import { GroupDraftCard } from './SearchDraftCards';
import { emptyAdsOf, useSearchAi } from './useSearchAi';

const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);

export default function SearchAdgroupPage({ objective: cfg }: { objective: SearchObjectiveConfig }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? '';

  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [tags, setTags] = useState<Record<string, SearchObjectiveKey>>({});
  const [leadFormIds, setLeadFormIds] = useState<Set<string>>(() => new Set());
  const [campaignId, setCampaignId] = useState(params.get('campaign_id') || '');
  const [defaultUrl, setDefaultUrl] = useState('');
  const [existingGroups, setExistingGroups] = useState<number | null>(null);
  const campaign = campaigns?.find((c) => c.id === campaignId);
  const manualCpc = isManualCpc(campaign?.biddingStrategyType);

  const first = useMemo(() => newSearchGroup(1), []);
  const [groups, setGroups] = useState<SearchGroupDraft[]>([first]);
  const [openGroup, setOpenGroup] = useState(first.id);
  const [activeAd, setActiveAd] = useState(first.ads[0].id);
  const patchGroup = (gid: string, p: Partial<SearchGroupDraft>) => setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...p } : g)));

  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const madeRef = useRef<{ groups: Record<string, string>; ads: Record<string, string> }>({ groups: {}, ads: {} });

  useEffect(() => {
    if (!accountUid) return;
    Promise.all([
      googleAdsService.listCampaigns(accountUid),
      googleAdsService.search.campaignObjectives(accountUid).catch(() => ({} as Record<string, SearchObjectiveKey>)),
      googleAdsService.search.leadFormCampaigns(accountUid).catch(() => [] as string[]),
    ]).then(([list, t, lf]) => {
      setCampaigns(list.filter((c) => isSearchCampaign(c) && c.status !== 'REMOVED'));
      setTags(t);
      setLeadFormIds(new Set(lf));
    }).catch(() => setCampaigns([]));
  }, [accountUid]);

  // The campaign's existing ads tell us its landing page — new ads start from it.
  useEffect(() => {
    setExistingGroups(null);
    if (!accountUid || !campaignId) return;
    let alive = true;
    (async () => {
      try {
        const gs = (await googleAdsService.listAdGroups(accountUid, campaignId)).filter((g) => g.status !== 'REMOVED');
        if (!alive) return;
        setExistingGroups(gs.length);
        for (const g of gs.slice(0, 3)) {
          const ads = await googleAdsService.listAds(accountUid, g.id);
          const url = ads.map((a) => a.finalUrls[0]).find((u) => u && isHttpUrl(u));
          if (url) {
            if (!alive) return;
            setDefaultUrl(url);
            setGroups((cur) => cur.map((x) => ({ ...x, ads: x.ads.map((a) => (a.finalUrl ? a : { ...a, finalUrl: url })) })));
            return;
          }
        }
      } catch { if (alive) setExistingGroups(null); }
    })();
    return () => { alive = false; };
  }, [accountUid, campaignId]);

  const ai = useSearchAi({ objective: cfg.key, setGroups, siteUrl: defaultUrl });
  const emptyAds = useMemo(() => emptyAdsOf(groups, (id) => !!madeRef.current.ads[id]), [groups, pub]); // eslint-disable-line react-hooks/exhaustive-deps

  const mine = (campaigns ?? []).filter((c) => searchObjectiveOf(c, tags, leadFormIds) === cfg.key);
  const others = (campaigns ?? []).filter((c) => searchObjectiveOf(c, tags, leadFormIds) !== cfg.key);

  const issues = useMemo(() => {
    const i: string[] = [];
    if (!campaignId) i.push('Pick the campaign these ad groups belong to.');
    groups.forEach((g, n) => {
      const label = g.name.trim() || `Ad group ${n + 1}`;
      const gi = searchGroupIssues(g, manualCpc);
      if (gi.length) i.push(`${label}: needs ${gi.join(', ')}.`);
      g.ads.forEach((a, k) => { const ai2 = rsaIssues(a); if (ai2.length) i.push(`${label} → ${a.name.trim() || `Ad ${k + 1}`}: needs ${ai2.join(', ')}.`); });
    });
    return i;
  }, [campaignId, groups, manualCpc]);

  const submit = async () => {
    if (!accountUid || issues.length) return;
    setSaving(true); setError(null);
    const mark = (k: string, v: Pub) => setPub((m) => ({ ...m, [k]: v }));
    const made = madeRef.current;
    try {
      for (const g of groups) {
        if (!made.groups[g.id]) {
          mark(g.id, 'running');
          try {
            const r = await googleAdsService.search.createAdGroup(accountUid, {
              campaignId, name: g.name.trim(), status: 'PAUSED', cpcBidMicros: manualCpc ? toMicros(g.cpcBid) : undefined,
              keywords: g.keywords.map((k) => ({ text: k.text, matchType: k.matchType })),
              negativeKeywords: g.negatives.map((k) => ({ text: k.text, matchType: k.matchType })),
            });
            made.groups[g.id] = r.adGroupId;
          } catch (e) { mark(g.id, 'failed'); setError(`Ad group "${g.name.trim()}": ${errMsg(e)}`); return; }
          mark(g.id, 'done');
        }
        for (const a of g.ads) {
          if (made.ads[a.id]) continue;
          mark(a.id, 'running');
          try {
            const r = await googleAdsService.search.createAd(accountUid, {
              adGroupId: made.groups[g.id], finalUrls: [a.finalUrl.trim()], headlines: filled(a.headlines), descriptions: filled(a.descriptions),
              path1: a.path1 || undefined, path2: a.path2 || undefined, status: 'PAUSED',
            });
            made.ads[a.id] = r.resourceName;
          } catch (e) { mark(a.id, 'failed'); setError(`Ad "${filled(a.headlines)[0] ?? a.name}" in ${g.name.trim()}: ${errMsg(e)}`); return; }
          mark(a.id, 'done');
        }
      }
      toast.success(groups.length === 1 ? 'Ad group created — paused until you enable it.' : `${groups.length} ad groups created — paused until you enable them.`);
      navigate(cfg.base);
    } finally { setSaving(false); }
  };

  const allAds = groups.flatMap((g) => g.ads.map((a) => ({ g, a })));
  const active = allAds.find((x) => x.a.id === activeAd) ?? allAds[0] ?? null;
  const st = campaign ? statusOf(campaign.status, campaign.primaryStatus) : null;

  return (
    <>
      <Topbar title="New ad groups" breadcrumb={`Google Ads / ${cfg.title}`}
        description="Each ad group is one theme — its own keywords and ads"
        back={<BackTo to={cfg.base} label={`Back to ${cfg.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && (
          <div className="flex min-h-[50vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>
        )}
        {auth !== undefined && !ready && (
          <EmptyState icon={<Globe size={36} className="text-text-muted" />} title="Connect a Google Ads account first"
            description="Authorise Google and choose a customer account, then come back."
            action={<Link to="/google-ads/account"><Button iconLeft={<Globe size={14} />}>Connect Google Ads</Button></Link>} />
        )}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <div className="min-w-0 flex-1 space-y-4">
              <Card padded={false} className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2.5 text-xs text-text-muted">
                <span>Goal: <span className="font-medium text-text-primary">{cfg.goal}</span></span>
                <span>Type: <span className="font-medium text-text-primary">Search</span></span>
                <span>Adding to: <span className="font-medium text-text-primary">{campaign?.name ?? 'pick a campaign'}</span></span>
                <Tag>starts paused</Tag>
              </Card>

              <Section title="Campaign" hint="The Search campaign these ad groups belong to.">
                <Select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} disabled={Object.keys(madeRef.current.groups).length > 0}>
                  <option value="">{campaigns === null ? 'Loading campaigns…' : 'Pick a Search campaign'}</option>
                  {mine.length > 0 && <optgroup label={`${cfg.title} campaigns`}>{mine.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
                  {others.length > 0 && <optgroup label="Other Search campaigns">{others.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
                </Select>
                {campaign && st && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-2xs text-text-muted">
                    <Pill {...st} />
                    {biddingLabel(campaign.biddingStrategyType) && <Tag>{biddingLabel(campaign.biddingStrategyType)}</Tag>}
                    {formatMicros(campaign.budgetAmountMicros, currency) && <Tag>{formatMicros(campaign.budgetAmountMicros, currency)} / day</Tag>}
                    {leadFormIds.has(campaign.id) && <Tag tone="ok">Lead form</Tag>}
                    {existingGroups !== null && <span>{existingGroups} ad group{existingGroups === 1 ? '' : 's'} already</span>}
                    {manualCpc && <span className="text-text-secondary">· Manual CPC — each new group needs a max CPC</span>}
                  </div>
                )}
              </Section>

              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary/30 bg-primary-soft/40 px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary"><Sparkles size={16} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-text-primary">Write keywords and ads with AI</div>
                  <div className="text-2xs text-text-muted">Uses each ad's landing page and the group's keywords · "Suggest with AI" adds keyword ideas, "Write with AI" drafts an ad.</div>
                </div>
                <Button size="sm" variant={emptyAds.length ? 'primary' : 'secondary'} iconLeft={<Wand2 size={13} />} loading={ai.busy === 'all'}
                  disabled={!!ai.busy || emptyAds.length === 0} onClick={() => ai.writeAll(emptyAds)}>
                  {emptyAds.length ? `Write ${emptyAds.length} empty ad${emptyAds.length === 1 ? '' : 's'}` : 'All ads have copy'}
                </Button>
              </div>

              {groups.map((g, n) => (
                <GroupDraftCard key={g.id} group={g} index={n} open={openGroup === g.id} onToggle={() => setOpenGroup(openGroup === g.id ? '' : g.id)}
                  manualCpc={manualCpc} currency={currency} pub={pub} madeGroup={!!madeRef.current.groups[g.id]} madeAd={(id) => !!madeRef.current.ads[id]}
                  busy={saving} onChange={(p) => patchGroup(g.id, p)} defaultUrl={defaultUrl}
                  onDuplicate={() => { const c = cloneSearchGroup(g); setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), c, ...gs.slice(i + 1)]; }); setOpenGroup(c.id); }}
                  onRemove={groups.length > 1 ? () => setGroups((gs) => gs.filter((x) => x.id !== g.id)) : undefined}
                  activeAdId={active?.a.id} onFocusAd={setActiveAd}
                  onSuggest={() => ai.suggest(g)} suggesting={ai.busy === `kw:${g.id}`}
                  onWriteAd={(a) => ai.write(g, a)} writingAd={(id) => ai.busy === `ad:${id}` || (ai.busy === 'all' && emptyAds.some((x) => x.a.id === id))}
                  aiAds={ai.aiAds} onAdCopyEdited={ai.copyEdited} />
              ))}

              <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={saving}
                onClick={() => { const g = newSearchGroup(groups.length + 1, defaultUrl); setGroups((gs) => [...gs, g]); setOpenGroup(g.id); setActiveAd(g.ads[0].id); }}>
                Add another ad group
              </Button>

              {issues.length > 0 && !error && (
                <ul className="space-y-1 rounded-xl border border-warning/30 bg-warning-soft/40 px-4 py-3">
                  {issues.slice(0, 6).map((i) => <li key={i} className="text-2xs text-warning">{i}</li>)}
                  {issues.length > 6 && <li className="text-2xs text-warning">+{issues.length - 6} more</li>}
                </ul>
              )}
              {error && (
                <div>
                  <ErrorBox message={error} />
                  <p className="mt-1 text-2xs text-text-muted">Fix it and try again — anything already created is kept, not repeated.</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="secondary" onClick={() => navigate(cfg.base)} disabled={saving}>Cancel</Button>
                <Button onClick={submit} loading={saving} disabled={saving || issues.length > 0} iconLeft={<Check size={15} />}>
                  {error ? 'Retry' : groups.length === 1 ? 'Create ad group' : `Create ${groups.length} ad groups`}
                </Button>
              </div>
            </div>

            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24"><SearchAdPreview ad={active?.a ?? null} keywords={active?.g.keywords ?? []} /></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
